export const LOCALIZATION_FILE_SOURCE_FILE = 'file';
export const LOCALIZATION_FILE_SOURCE_INHERIT = 'inherit';
export const LOCALIZATION_FILE_FORMAT_HTML = 'html';
export const LOCALIZATION_FILE_STORAGE_FORMAT_JSON = 'json';

const manifestCache = new Map();
const localizationFileCache = new Map();

const LOCALIZATION_FILE_DEFINITIONS = {
  faq: {
    label: 'FAQ',
    description: '',
    format: LOCALIZATION_FILE_FORMAT_HTML,
    systemDefaults: {
      en: [
        '<h4>How do I get my pictures and videos?</h4>',
        '<p>We use state of the art face recognition technology that will find photos and videos we made of you and your party throughout your experience. To take advantage of this feature, you must register portrait photos (selfies) of each member of your party.</p>',
        '<h4>What does a Storibox Pass include?</h4>',
        '<p>With a Storibox Pass, you may</p>',
        '<ul><li>Download and save high-resolution images and videos, based on which package you purchased.</li><li>Instantly share your signature memories with your friends and family.</li><li>Access your content for up to 60 days post your visit.</li></ul>',
        '<h4>I have purchased a Storibox Pass. When can I begin downloading my photos?</h4>',
        '<p>With a Storibox Pass, you may begin downloading as soon as photos are available in your account. You may choose to download them immediately or after your trip, because the photos will be available for up to 60 days from the day of your visit.</p>',
        '<h4>I have not decided to get a Storibox Pass. Can I still view my images?</h4>',
        '<p>If you did not purchase a digital pass, you can still preview your images, but they will be watermarked.</p>',
      ].join(''),
      fr: [
        '<h4>Comment puis-je récupérer mes photos et vidéos ?</h4>',
        '<p>Nous utilisons une technologie de reconnaissance faciale de pointe qui retrouvera les photos et vidéos prises de vous et de votre groupe tout au long de votre expérience. Pour profiter de cette fonctionnalité, vous devez enregistrer des photos portrait (selfies) de chaque membre de votre groupe.</p>',
        '<h4>Que comprend un Pass Storibox ?</h4>',
        '<p>Avec un Pass Storibox, vous pouvez</p>',
        '<ul><li>Télécharger et enregistrer des images et vidéos en haute résolution, selon le forfait que vous avez acheté.</li><li>Partager instantanément vos souvenirs inoubliables avec vos proches.</li><li>Accéder à votre contenu jusqu\'à 60 jours après votre visite.</li></ul>',
        '<h4>J\'ai acheté un Pass Storibox. Quand puis-je commencer à télécharger mes photos ?</h4>',
        '<p>Avec un Pass Storibox, vous pouvez commencer à télécharger dès que les photos sont disponibles dans votre compte. Vous pouvez choisir de les télécharger immédiatement ou après votre voyage, car elles resteront disponibles jusqu\'à 60 jours à compter du jour de votre visite.</p>',
        '<h4>Je n\'ai pas encore décidé de prendre un Pass Storibox. Puis-je quand même voir mes images ?</h4>',
        '<p>Si vous n\'avez pas acheté de pass numérique, vous pouvez tout de même prévisualiser vos images, mais elles seront filigranées.</p>',
      ].join(''),
    },
  },
  privacy: {
    label: 'Terms and Conditions',
    description: '',
    format: LOCALIZATION_FILE_FORMAT_HTML,
    systemDefaults: {
      en: '',
      fr: '',
    },
  },
};

/**
 * Returns the registry for the standalone localization-files system.
 *
 * Example input:
 * - no arguments
 *
 * Example output:
 * - an array with `faq` and `privacy` definitions
 *
 * Edge cases:
 * - returns an empty array if no large content items are registered
 */
export function getLocalizationFileDefinitions() {
  return Object.entries(LOCALIZATION_FILE_DEFINITIONS).map(([contentId, definition]) => ({
    contentId,
    ...definition,
  }));
}

/**
 * Returns a single localization-files definition by content id.
 *
 * Example input:
 * - `contentId = 'faq'`
 *
 * Example output:
 * - the FAQ definition object
 *
 * Edge cases:
 * - returns `null` when the content id is not registered
 */
export function getLocalizationFileDefinition(contentId) {
  const definition = LOCALIZATION_FILE_DEFINITIONS[contentId];
  return definition ? { contentId, ...definition } : null;
}

/**
 * Returns repo-owned fallback HTML for a localization file.
 *
 * Example input:
 * - `contentId = 'privacy'`
 * - `language = 'fr-CA'`
 *
 * Example output:
 * - French privacy HTML fallback
 *
 * Edge cases:
 * - falls back to English when the requested language is not defined
 * - returns an empty string when the content id is unknown
 */
export function getSystemDefaultLocalizationFile({ contentId, language }) {
  const normalizedLanguage = normalizeLanguage(language);
  const definition = LOCALIZATION_FILE_DEFINITIONS[contentId];

  if (!definition) {
    return '';
  }

  return definition.systemDefaults[normalizedLanguage] ?? definition.systemDefaults.en ?? '';
}

export function buildLocalizationFilesManifestFolderPath({ orgId }) {
  return `${orgId}/localization-files/`;
}

export function buildLocalizationFilesManifestRelativePath({ orgId }) {
  return `${buildLocalizationFilesManifestFolderPath({ orgId })}manifest.json`;
}

export function buildLocalizationFileFolderPath({ orgId, eventCode = 'global', language }) {
  const normalizedLanguage = normalizeLanguage(language);
  return `${orgId}/localization-files/${getScopePath(eventCode)}/${normalizedLanguage}/`;
}

export function buildLocalizationFileRelativePath({ orgId, eventCode = 'global', language, contentId }) {
  return `${buildLocalizationFileFolderPath({ orgId, eventCode, language })}${contentId}.${LOCALIZATION_FILE_STORAGE_FORMAT_JSON}`;
}

export function getLocalizationFilesManifestUrl(params) {
  return `${process.env.LOCALIZATION_CDN}/${buildLocalizationFilesManifestRelativePath(params)}`;
}

export function getLocalizationFileUrl(params) {
  return `${process.env.LOCALIZATION_CDN}/${buildLocalizationFileRelativePath(params)}`;
}

/**
 * Fetches a manifest that describes whether each content id inherits or uses its own file.
 *
 * Example input:
 * - `orgId = 12`
 *
 * Example output:
 * - `{ faq: { global: { en: { source: 'file', format: 'html' } }, wmww: { fr: { source: 'file' } } } }`
 *
 * Edge cases:
 * - returns `{}` on 404, invalid JSON, or network errors
 */
export async function fetchLocalizationFilesManifest(
  { orgId },
  { fetcher = globalThis.fetch, force = false } = {}
) {
  if (typeof fetcher !== 'function') {
    return {};
  }

  const cacheKey = String(orgId);

  if (!force && manifestCache.has(cacheKey)) {
    return manifestCache.get(cacheKey);
  }

  const manifestPromise = (async () => {
    try {
      const response = await fetcher(getLocalizationFilesManifestUrl({ orgId }), {
        method: 'GET',
        cache: 'no-cache',
        mode: 'cors',
      });

      if (!response.ok) {
        return {};
      }

      const data = await response.json();
      return normalizeManifest(data);
    } catch (error) {
      console.error(`Error fetching localization files manifest for organisation "${orgId}":`, error);
      return {};
    }
  })();

  manifestCache.set(cacheKey, manifestPromise);

  return manifestPromise;
}

/**
 * Fetches stored HTML content for a localization file.
 *
 * Example input:
 * - `orgId = 12`
 * - `eventCode = 'global'`
 * - `language = 'en'`
 * - `contentId = 'faq'`
 *
 * Example output:
 * - stored HTML string for that exact scope
 *
 * Edge cases:
 * - returns an empty string when the file does not exist or cannot be fetched
 */
export async function fetchStoredLocalizationFile(
  { orgId, eventCode = 'global', language, contentId },
  { fetcher = globalThis.fetch, force = false } = {}
) {
  if (typeof fetcher !== 'function') {
    return '';
  }

  const cacheKey = `${orgId}:${eventCode || 'global'}:${normalizeLanguage(language)}:${contentId}`;

  if (!force && localizationFileCache.has(cacheKey)) {
    return localizationFileCache.get(cacheKey);
  }

  const filePromise = (async () => {
    try {
      const response = await fetcher(getLocalizationFileUrl({ orgId, eventCode, language, contentId }), {
        method: 'GET',
        cache: 'no-cache',
        mode: 'cors',
      });

      if (!response.ok) {
        return '';
      }

      const rawBody = await response.text();
      return parseStoredLocalizationFile(rawBody);
    } catch (error) {
      console.error(`Error fetching localization file "${contentId}":`, error);
      return '';
    }
  })();

  localizationFileCache.set(cacheKey, filePromise);

  return filePromise;
}

export function getManifestEntry({ manifest = {}, eventCode = 'global', contentId, language }) {
  const normalizedLanguage = normalizeLanguage(language);
  const scopeKey = eventCode || 'global';
  return manifest?.[contentId]?.[scopeKey]?.[normalizedLanguage] || {
    source: LOCALIZATION_FILE_SOURCE_INHERIT,
    hasContent: false,
  };
}

/**
 * Resolves HTML content through event -> global -> system default fallback.
 *
 * Example input:
 * - `orgId = 12`
 * - `eventCode = 'wmww'`
 * - `language = 'fr'`
 * - `contentId = 'privacy'`
 *
 * Example output:
 * - resolved HTML string for runtime rendering
 *
 * Edge cases:
 * - `source = inherit` falls through to the next layer
 * - missing or empty stored files fall through to the next layer
 * - returns an empty string when every layer resolves to empty content
 */
export async function resolveLocalizationFile(
  { orgId, eventCode = 'global', language, contentId },
  { fetcher = globalThis.fetch, force = false } = {}
) {
  const normalizedLanguage = normalizeLanguage(language);
  const resolvedEventCode = eventCode || 'global';
  const manifest = await fetchLocalizationFilesManifest({ orgId }, { fetcher, force });

  if (resolvedEventCode !== 'global') {
    const eventEntry = getManifestEntry({
      manifest,
      eventCode: resolvedEventCode,
      contentId,
      language: normalizedLanguage,
    });

    if (eventEntry.source === LOCALIZATION_FILE_SOURCE_FILE) {
      const eventValue = await fetchStoredLocalizationFile(
        { orgId, eventCode: resolvedEventCode, language: normalizedLanguage, contentId },
        { fetcher, force }
      );

      if (hasResolvedLocalizationFileContent(eventValue)) {
        return eventValue;
      }
    }
  }

  const globalEntry = getManifestEntry({
    manifest,
    eventCode: 'global',
    contentId,
    language: normalizedLanguage,
  });

  if (globalEntry.source === LOCALIZATION_FILE_SOURCE_FILE) {
    const globalValue = await fetchStoredLocalizationFile(
      { orgId, eventCode: 'global', language: normalizedLanguage, contentId },
      { fetcher, force }
    );

    if (hasResolvedLocalizationFileContent(globalValue)) {
      return globalValue;
    }
  }

  return getSystemDefaultLocalizationFile({ contentId, language: normalizedLanguage });
}

export async function hasResolvedLocalizationFile(params, options) {
  const { orgId, eventCode = 'global', language, contentId } = params;

  if (!orgId) {
    return hasResolvedLocalizationFileContent(getSystemDefaultLocalizationFile({ contentId, language }));
  }

  const manifest = await fetchLocalizationFilesManifest({ orgId }, options);
  const resolvedEventCode = eventCode || 'global';

  if (resolvedEventCode !== 'global') {
    const eventEntry = getManifestEntry({ manifest, eventCode: resolvedEventCode, contentId, language });

    if (eventEntry.source === LOCALIZATION_FILE_SOURCE_FILE) {
      return eventEntry.hasContent !== false;
    }
  }

  const globalEntry = getManifestEntry({ manifest, eventCode: 'global', contentId, language });

  if (globalEntry.source === LOCALIZATION_FILE_SOURCE_FILE) {
    return globalEntry.hasContent !== false;
  }

  return hasResolvedLocalizationFileContent(getSystemDefaultLocalizationFile({ contentId, language }));
}

export function hasResolvedLocalizationFileContent(content = '') {
  return content.trim() !== '';
}

export function resetLocalizationFilesCache({ orgId } = {}) {
  if (!orgId) {
    manifestCache.clear();
    localizationFileCache.clear();
    return;
  }

  const orgPrefix = `${orgId}:`;
  manifestCache.delete(String(orgId));

  Array.from(localizationFileCache.keys()).forEach((cacheKey) => {
    if (cacheKey.startsWith(orgPrefix)) {
      localizationFileCache.delete(cacheKey);
    }
  });
}

function getScopePath(eventCode = 'global') {
  return eventCode === 'global' ? 'global' : `events/${eventCode}`;
}

function normalizeLanguage(language = 'en') {
  return `${language || 'en'}`.split('-')[0];
}

function parseStoredLocalizationFile(rawBody = '') {
  if (typeof rawBody !== 'string' || rawBody.trim() === '') {
    return '';
  }

  try {
    const parsed = JSON.parse(rawBody);

    if (typeof parsed === 'string') {
      return parsed;
    }

    if (parsed && typeof parsed.content === 'string') {
      return parsed.content;
    }
  } catch (_error) {
    return rawBody;
  }

  return '';
}

function normalizeManifest(manifest = {}) {
  if (!manifest || typeof manifest !== 'object' || Array.isArray(manifest)) {
    return {};
  }

  return normalizeFlatManifest(manifest);
}

function normalizeFlatManifest(manifest = {}) {
  const normalizedManifest = {};

  Object.entries(manifest).forEach(([contentId, scopeMap]) => {
    if (!isPlainObject(scopeMap)) {
      return;
    }

    const normalizedScopeMap = {};

    Object.entries(scopeMap).forEach(([scopeKey, languageMap]) => {
      const normalizedLanguageMap = normalizeLanguageMap(languageMap);

      if (Object.keys(normalizedLanguageMap).length > 0) {
        normalizedScopeMap[scopeKey || 'global'] = normalizedLanguageMap;
      }
    });

    if (Object.keys(normalizedScopeMap).length > 0) {
      normalizedManifest[contentId] = normalizedScopeMap;
    }
  });

  return normalizedManifest;
}

function normalizeLanguageMap(languageMap = {}) {
  if (!isPlainObject(languageMap)) {
    return {};
  }

  const normalizedLanguageMap = {};

  Object.entries(languageMap).forEach(([language, value]) => {
    if (!isPlainObject(value)) {
      return;
    }

    normalizedLanguageMap[normalizeLanguage(language)] = value;
  });

  return normalizedLanguageMap;
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
